/**
 * Seeded gradient noise, sampled on a cylinder.
 *
 * The map wraps east-west, so noise sampled on a flat plane would leave a visible
 * seam where column 0 meets column width-1 — a straight line of mismatched
 * coastline running pole to pole. The fix is to sample 3D noise on the surface of a
 * cylinder: a tile's column becomes an angle, and the noise is read at
 * (cos θ · r, sin θ · r, y). Going all the way around returns to the same point, so
 * the seam cannot exist rather than merely being hidden.
 *
 * Classic Perlin gradient noise, with the permutation table shuffled by the game's
 * own seeded generator so a seed reproduces a world exactly.
 */

import { nextInt, type RngState } from '../rng/index.js';

/** The twelve gradient directions of classic 3D Perlin noise (edge midpoints of a cube). */
const GRADIENTS: ReadonlyArray<readonly [number, number, number]> = [
  [1, 1, 0], [-1, 1, 0], [1, -1, 0], [-1, -1, 0],
  [1, 0, 1], [-1, 0, 1], [1, 0, -1], [-1, 0, -1],
  [0, 1, 1], [0, -1, 1], [0, 1, -1], [0, -1, -1],
];

export interface NoiseField {
  /** Raw noise at a point, in roughly [-1, 1]. */
  at(x: number, y: number, z: number): number;
}

/** Perlin's smoothstep-of-smoothstep fade: 6t⁵ - 15t⁴ + 10t³. */
function fade(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function lerp(a: number, b: number, t: number): number {
  return a + t * (b - a);
}

function dot(gradient: readonly [number, number, number], x: number, y: number, z: number): number {
  return gradient[0] * x + gradient[1] * y + gradient[2] * z;
}

/** Build a noise field whose permutation table is shuffled by `rng`. */
export function createNoise(rng: RngState): NoiseField {
  // 256 entries, doubled so lookups never need a modulo.
  const permutation = new Uint8Array(512);
  const source = new Uint8Array(256);
  for (let i = 0; i < 256; i++) source[i] = i;
  for (let i = 255; i > 0; i--) {
    const j = nextInt(rng, 0, i + 1);
    const swap = source[i]!;
    source[i] = source[j]!;
    source[j] = swap;
  }
  for (let i = 0; i < 512; i++) permutation[i] = source[i & 255]!;

  return {
    at(x: number, y: number, z: number): number {
      const xi = Math.floor(x) & 255;
      const yi = Math.floor(y) & 255;
      const zi = Math.floor(z) & 255;
      const xf = x - Math.floor(x);
      const yf = y - Math.floor(y);
      const zf = z - Math.floor(z);

      const u = fade(xf);
      const v = fade(yf);
      const w = fade(zf);

      const a = permutation[xi]! + yi;
      const aa = permutation[a]! + zi;
      const ab = permutation[a + 1]! + zi;
      const b = permutation[xi + 1]! + yi;
      const ba = permutation[b]! + zi;
      const bb = permutation[b + 1]! + zi;

      const g = (hash: number) => GRADIENTS[permutation[hash]! % 12]!;

      return lerp(
        lerp(
          lerp(dot(g(aa), xf, yf, zf), dot(g(ba), xf - 1, yf, zf), u),
          lerp(dot(g(ab), xf, yf - 1, zf), dot(g(bb), xf - 1, yf - 1, zf), u),
          v,
        ),
        lerp(
          lerp(dot(g(aa + 1), xf, yf, zf - 1), dot(g(ba + 1), xf - 1, yf, zf - 1), u),
          lerp(dot(g(ab + 1), xf, yf - 1, zf - 1), dot(g(bb + 1), xf - 1, yf - 1, zf - 1), u),
          v,
        ),
        w,
      );
    },
  };
}

export interface FbmOptions {
  octaves: number;
  /** How much finer each successive octave is. 2 is the usual choice. */
  lacunarity?: number;
  /** How much quieter each successive octave is. Below 0.5 gives smooth terrain, above gives rough. */
  gain?: number;
}

/**
 * Fractal Brownian motion: sum several octaves of noise at doubling frequency and
 * halving amplitude. One octave is featureless blobs; five gives coastlines with
 * both broad shape and fine detail. Result is normalised to roughly [-1, 1].
 */
export function fbm(field: NoiseField, x: number, y: number, z: number, options: FbmOptions): number {
  const lacunarity = options.lacunarity ?? 2;
  const gain = options.gain ?? 0.5;

  let total = 0;
  let amplitude = 1;
  let frequency = 1;
  let normaliser = 0;

  for (let octave = 0; octave < options.octaves; octave++) {
    total += field.at(x * frequency, y * frequency, z * frequency) * amplitude;
    normaliser += amplitude;
    amplitude *= gain;
    frequency *= lacunarity;
  }

  return normaliser === 0 ? 0 : total / normaliser;
}

/**
 * Maps a tile's column and row onto a cylinder and samples fbm there.
 *
 * `scale` is roughly "how many noise features fit around the world" — larger means
 * smaller, more numerous landmasses. Rows map to the cylinder's axis directly, so
 * north-south does not wrap, which is correct: the poles are not connected.
 */
export function cylindricalFbm(
  field: NoiseField,
  col: number,
  row: number,
  width: number,
  scale: number,
  options: FbmOptions,
): number {
  const theta = (col / width) * Math.PI * 2;
  // Radius chosen so horizontal and vertical feature sizes match: the cylinder's
  // circumference in noise units must equal the map's width in noise units.
  const radius = scale / (Math.PI * 2);
  return fbm(field, Math.cos(theta) * radius, Math.sin(theta) * radius, (row / width) * scale, options);
}

/**
 * Domain warping: displace the sample point by another noise field before reading.
 * This is what turns the recognisably circular blobs of raw noise into coastlines
 * with peninsulas, bays and fjords. Cheap, and the single biggest visual win in the
 * whole generator.
 */
export function warpedCylindricalFbm(
  field: NoiseField,
  warp: NoiseField,
  col: number,
  row: number,
  width: number,
  scale: number,
  warpStrength: number,
  options: FbmOptions,
): number {
  const warpX = cylindricalFbm(warp, col, row, width, scale * 2, { octaves: 2 }) * warpStrength;
  const warpY = cylindricalFbm(warp, col + width * 0.37, row + 91.3, width, scale * 2, { octaves: 2 }) * warpStrength;
  return cylindricalFbm(field, col + warpX, row + warpY, width, scale, options);
}
